import {
  clearMobileSession,
  createSessionStorage,
  detectNativeMobilePlatform,
  extractMobileExchange,
  mobileSessionToDomainSession,
  persistMobileSession,
  restoreMobileSession,
  type MobileSessionPayload,
} from "@fluentry/platform-adapters";
import { authSessionToDomainSession } from "@fluentry/shared-workbench/auth";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar";
import React from "react";
import type { ProductAppAuthSessionData } from "../lib/auth-client";
import { buildNativeBrowserLoginUrl, resolveMobileBridgeConfig } from "../lib/mobile-bridge";
import { createNativeSecureStorage } from "../lib/native-secure-storage";
import { captureProductAppException } from "../lib/sentry";

export function useAppSessionBridge(authSession: ProductAppAuthSessionData) {
  const nativePlatform = React.useMemo(() => detectNativeMobilePlatform(), []);
  const mobileBridgeConfig = React.useMemo(() => resolveMobileBridgeConfig(), []);
  const isNativeSurface = nativePlatform !== null;
  const sessionStorage = React.useMemo(
    () => createSessionStorage(isNativeSurface ? createNativeSecureStorage() : undefined),
    [isNativeSurface],
  );
  const [mobileSession, setMobileSessionState] = React.useState<MobileSessionPayload | null>(null);
  const [mobileReady, setMobileReady] = React.useState(false);
  const [mobileBridgeError, setMobileBridgeError] = React.useState<string | null>(null);
  const openBrowserInFlightRef = React.useRef(false);
  const handledMobileCallbackRef = React.useRef<string | null>(null);

  // Keep session derivation inline so Compiler can optimize the pure projection while the bridge callbacks stay explicitly stable.
  const session = mobileSession
    ? mobileSessionToDomainSession(mobileSession)
    : authSessionToDomainSession(authSession);

  const setMobileSession = React.useCallback(
    async (nextSession: MobileSessionPayload | null) => {
      // Persist the mobile bridge token here so every shell path uses the same storage semantics.
      if (nextSession) {
        await persistMobileSession(sessionStorage, nextSession);
      } else {
        await clearMobileSession(sessionStorage);
      }

      setMobileSessionState(nextSession);
    },
    [sessionStorage],
  );

  function clearMobileBridgeError() {
    setMobileBridgeError(null);
  }

  const openNativeBrowserSignIn = React.useCallback(async () => {
    if (!nativePlatform) {
      return;
    }

    if (openBrowserInFlightRef.current) {
      return;
    }

    openBrowserInFlightRef.current = true;
    setMobileBridgeError(null);
    try {
      await Browser.open({
        url: buildNativeBrowserLoginUrl(mobileBridgeConfig, nativePlatform),
        toolbarColor: "#f8fafc",
        presentationStyle: "fullscreen",
      });
    } finally {
      openBrowserInFlightRef.current = false;
    }
  }, [mobileBridgeConfig, nativePlatform]);

  React.useEffect(() => {
    // Centralize native chrome setup and session restore here so AppShell stays focused on routing and rendering only.
    if (!isNativeSurface) {
      setMobileReady(true);
      return;
    }

    let active = true;
    const handles: Array<PromiseLike<{ remove: () => Promise<void> | void }>> = [];

    void (async () => {
      try {
        await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => undefined);
        await StatusBar.setBackgroundColor({ color: "#f8fafc" }).catch(() => undefined);
        await StatusBar.setStyle({ style: StatusBarStyle.Light }).catch(() => undefined);
        await Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => undefined);
        await Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => undefined);

        handles.push(
          Keyboard.addListener("keyboardWillShow", ({ keyboardHeight }) => {
            document.documentElement.style.setProperty(
              "--mobile-keyboard-offset",
              `${keyboardHeight}px`,
            );
          }),
        );
        handles.push(
          Keyboard.addListener("keyboardWillHide", () => {
            document.documentElement.style.setProperty("--mobile-keyboard-offset", "0px");
          }),
        );

        const nextSession = await restoreMobileSession(sessionStorage);
        if (active) {
          setMobileSessionState(nextSession);
        }
      } catch (restoreError) {
        void captureProductAppException(restoreError, {
          action: "restore-mobile-session",
          feature: "session-bridge",
          tags: {
            native_platform: nativePlatform,
          },
        });
        await clearMobileSession(sessionStorage);
        if (active) {
          setMobileSessionState(null);
          setMobileBridgeError(
            restoreError instanceof Error
              ? restoreError.message
              : "Native session restore failed. Sign in again from the secure browser.",
          );
        }
      } finally {
        if (active) {
          setMobileReady(true);
        }
      }
    })();

    return () => {
      active = false;
      document.documentElement.style.setProperty("--mobile-keyboard-offset", "0px");
      for (const handle of handles) {
        void Promise.resolve(handle).then((listener) => listener.remove());
      }
    };
  }, [isNativeSurface, sessionStorage]);

  React.useEffect(() => {
    // Handle deep-link auth callbacks inside the bridge hook so the shell tree never parses native payloads directly.
    if (!isNativeSurface) {
      return;
    }

    const listener = CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      let exchangedSession: ReturnType<typeof extractMobileExchange>;
      try {
        exchangedSession = extractMobileExchange(url);
      } catch (error) {
        void captureProductAppException(error, {
          action: "mobile-callback-parse-failed",
          feature: "session-bridge",
          extras: {
            callback_url: url,
          },
          tags: {
            native_platform: nativePlatform,
          },
        });
        setMobileBridgeError(
          error instanceof Error
            ? error.message
            : "The mobile callback payload could not be parsed.",
        );
        void clearMobileSession(sessionStorage).finally(() => {
          setMobileSessionState(null);
          window.history.replaceState({}, "", "/login");
          window.location.href = "/login";
        });
        return;
      }

      if (!exchangedSession) {
        void captureProductAppException(new Error("Invalid mobile callback payload"), {
          action: "mobile-callback-invalid",
          feature: "session-bridge",
          extras: {
            callback_url: url,
          },
          tags: {
            native_platform: nativePlatform,
          },
        });
        setMobileBridgeError(
          "The mobile callback payload was invalid. Reopen sign in from the secure browser.",
        );
        void clearMobileSession(sessionStorage).finally(() => {
          setMobileSessionState(null);
          window.history.replaceState({}, "", "/login");
          window.location.href = "/login";
        });
        return;
      }

      const callbackKey = `${exchangedSession.token}:${exchangedSession.email}:${exchangedSession.platform}`;
      if (handledMobileCallbackRef.current === callbackKey) {
        return;
      }

      handledMobileCallbackRef.current = callbackKey;
      void setMobileSession(exchangedSession)
        .then(async () => {
          setMobileBridgeError(null);
          await Browser.close().catch(() => undefined);
          window.history.replaceState({}, "", "/");
          window.location.href = "/";
        })
        .catch((bridgeError) => {
          void captureProductAppException(bridgeError, {
            action: "restore-mobile-session-from-callback",
            feature: "session-bridge",
            tags: {
              native_platform: nativePlatform,
            },
          });
          setMobileBridgeError(
            bridgeError instanceof Error ? bridgeError.message : "Native session restore failed",
          );
          window.history.replaceState({}, "", "/login");
          window.location.href = "/login";
        });
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [isNativeSurface, sessionStorage, setMobileSession]);

  return {
    session,
    mobileSession,
    mobileReady,
    mobileBridgeConfig,
    mobileBridgeError,
    isNativeSurface,
    setMobileSession,
    clearMobileBridgeError,
    openNativeBrowserSignIn,
  };
}
